import { debounce } from './debounce.js';
/**
 * @module video-cover-resizer
 * Resizes iframes within specified containers to mimic CSS `object-fit: cover`.
 * This ensures the iframe content fills its container while maintaining aspect ratio,
 * clipping any overflow. For this to work visually, the container element
 * requires `position: relative; overflow: hidden;` and the iframe should have
 * `position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);`
 * in your stylesheet.
 */

/**
 * Calculates and applies new dimensions to iframes to make them cover their containers.
 * @param {string} containerSelector - The CSS selector for the container elements (e.g., '.video-container').
 */
export function resizeIframesToCover(containerSelector) {
    const containers = document.querySelectorAll(containerSelector);

    containers.forEach(container => {
        const iframe = container.querySelector('iframe');
        if (!iframe) {
            return;
        }

        // Ensure iframe has width and height attributes for aspect ratio calculation.
        const iframeWidth = parseFloat(iframe.getAttribute('width'));
        const iframeHeight = parseFloat(iframe.getAttribute('height'));

        if (isNaN(iframeWidth) || isNaN(iframeHeight) || iframeWidth === 0 || iframeHeight === 0) {
            // Don't log an error if attributes are missing, as they might not be present on all iframes.
            return;
        }

        const iframeAspectRatio = iframeWidth / iframeHeight;

        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;

        // Avoid division by zero if container has no height.
        if (containerHeight === 0) {
            return;
        }

        const containerAspectRatio = containerWidth / containerHeight;

        let newWidth, newHeight;

        // Compare aspect ratios to decide which dimension to match to cover the container.
        if (containerAspectRatio > iframeAspectRatio) {
            // Container is wider than the iframe's aspect ratio.
            // We must make the iframe's width match the container's width,
            // and the height will be proportionally larger, overflowing vertically.
            newWidth = containerWidth;
            newHeight = newWidth / iframeAspectRatio;
        } else {
            // Container is taller or has the same aspect ratio as the iframe.
            // We must make the iframe's height match the container's height,
            // and the width will be proportionally larger, overflowing horizontally.
            newHeight = containerHeight;
            newWidth = newHeight * iframeAspectRatio;
        }

        iframe.style.width = `${newWidth}px`;
        iframe.style.height = `${newHeight}px`;
    });
}

export function initializeVideoCoverResizerListeners(containerSelector = '.video-container') {
    // Debounce the resize handler to prevent it from firing excessively during window resizing.
    // This improves performance and prevents layout thrashing.
    const handleResize = debounce(() => resizeIframesToCover(containerSelector), 250);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
}